import React from 'react';
import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react';

import { EditorPopup, POPUP_Z_INDEX } from './DataTable.styles';

// A cell editor's popup (AG's cellEditorPopup), laid over the cell it edits
// and following it as the table scrolls. Pressing outside it or Escape
// cancels the edit, as AG's stopEditingWhenCellsLoseFocus does.
const CellEditorPopup = ({
  anchor,
  onCancel,
  children,
}: {
  anchor: HTMLElement;
  onCancel: () => void;
  children: React.ReactNode;
}) => {
  const { refs, floatingStyles, context } = useFloating({
    open: true,
    onOpenChange: (isOpen) => {
      if (!isOpen) {
        onCancel();
      }
    },
    elements: { reference: anchor },
    placement: 'bottom-start',
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    // Over the cell, as AG opens a popup editor
    middleware: [
      offset(({ rects }) => -rects.reference.height),
      flip(),
      shift({ padding: 8 }),
    ],
  });
  const dismiss = useDismiss(context);
  const { getFloatingProps } = useInteractions([dismiss]);

  return (
    <FloatingPortal>
      <EditorPopup
        ref={refs.setFloating}
        style={{ ...floatingStyles, zIndex: POPUP_Z_INDEX }}
        data-testid="data-table-cell-editor"
        {...getFloatingProps()}
      >
        {children}
      </EditorPopup>
    </FloatingPortal>
  );
};

export default CellEditorPopup;
